import { useState } from "react";

const STATUS_COLOR = { ok: "#22c55e", error: "#ef4444" };

export default function FetchLogViewer({ logs }) {
  const [expanded, setExpanded] = useState(null);

  if (!logs || logs.length === 0) {
    return <div style={styles.empty}>Noch keine Protokolleinträge vorhanden.<br />Der erste Abruf erfolgt beim Start automatisch.</div>;
  }

  return (
    <div style={styles.wrapper}>
      {logs.map((log, i) => {
        const isOpen = expanded === i;
        const hasErrors = log.errors > 0;
        const time = new Date(log.fetched_at).toLocaleString("de-CH", {
          day: "2-digit", month: "2-digit", year: "numeric",
          hour: "2-digit", minute: "2-digit", second: "2-digit",
        });

        return (
          <div key={i} style={{ ...styles.row, borderColor: hasErrors ? "#7f1d1d" : "#1e3a5f" }}>
            {/* Header-Zeile */}
            <div style={styles.rowHeader} onClick={() => setExpanded(isOpen ? null : i)}>
              <span style={{ ...styles.dot, background: hasErrors ? "#ef4444" : "#22c55e" }} />
              <span style={styles.time}>{time}</span>

              <div style={styles.pills}>
                <Pill color="#22c55e" label={`${log.success} OK`} />
                {hasErrors && <Pill color="#ef4444" label={`${log.errors} Fehler`} />}
                {log.duration_ms && <Pill color="#64748b" label={`${log.duration_ms}ms`} />}
              </div>

              {/* Mini-Status pro Sensor */}
              <div style={styles.miniSensors}>
                {(log.details || []).map((d, j) => (
                  <span
                    key={j}
                    title={`${d.sensor}: ${d.status === "ok" ? `${d.wind_speed} km/h` : d.msg}`}
                    style={{
                      ...styles.miniDot,
                      background: d.status === "ok" ? "#22c55e" : "#ef4444",
                    }}
                  />
                ))}
              </div>

              <span style={styles.chevron}>{isOpen ? "▲" : "▼"}</span>
            </div>

            {/* Detail-Tabelle */}
            {isOpen && log.details && log.details.length > 0 && (
              <div style={styles.detail}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      {["Sensor", "MAC", "Status", "Wind", "Böe", "Richtung", "Meldung"].map(h => (
                        <th key={h} style={styles.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {log.details.map((d, j) => (
                      <tr key={j}>
                        <td style={styles.td}><strong>{d.sensor}</strong></td>
                        <td style={{ ...styles.td, fontFamily: "monospace", fontSize: 11, color: "#64748b" }}>{d.mac}</td>
                        <td style={styles.td}>
                          <span style={{ color: STATUS_COLOR[d.status] ?? "#64748b", fontWeight: 700 }}>
                            {d.status === "ok" ? "✓ OK" : "✗ Fehler"}
                          </span>
                        </td>
                        <td style={styles.td}>
                          {d.wind_speed != null ? `${d.wind_speed} km/h` : "–"}
                        </td>
                        <td style={styles.td}>
                          {d.wind_gust != null ? `${d.wind_gust} km/h` : "–"}
                        </td>
                        <td style={styles.td}>
                          {d.wind_dir != null ? `${d.wind_dir}°` : "–"}
                        </td>
                        <td style={{ ...styles.td, color: "#ef4444", fontSize: 11 }}>
                          {d.msg || "–"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Pill({ color, label }) {
  return (
    <span style={{ background: color + "22", color, border: `1px solid ${color}55`, borderRadius: 4, padding: "1px 7px", fontSize: 11, fontWeight: 600 }}>
      {label}
    </span>
  );
}

const styles = {
  wrapper: { display: "flex", flexDirection: "column", gap: 6 },
  empty: { color: "#64748b", textAlign: "center", padding: "40px 0", lineHeight: 2 },
  row: { border: "1px solid", borderRadius: 8, overflow: "hidden" },
  rowHeader: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "10px 14px", cursor: "pointer",
    background: "#1e293b",
    "&:hover": { background: "#263548" },
  },
  dot: { width: 9, height: 9, borderRadius: "50%", flexShrink: 0 },
  time: { color: "#94a3b8", fontSize: 12, minWidth: 160 },
  pills: { display: "flex", gap: 5 },
  miniSensors: { display: "flex", gap: 3, marginLeft: 8 },
  miniDot: { width: 10, height: 10, borderRadius: "50%", flexShrink: 0 },
  chevron: { color: "#475569", fontSize: 11, marginLeft: "auto" },
  detail: { borderTop: "1px solid #334155", background: "#0f172a", padding: "12px 14px" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { color: "#475569", fontSize: 11, fontWeight: 600, textAlign: "left", padding: "6px 10px", borderBottom: "1px solid #1e3a5f" },
  td: { color: "#e2e8f0", fontSize: 12, padding: "8px 10px", borderBottom: "1px solid #0f172a" },
};
